import React, { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { parseMarkdownLocationLink } from '@aline/common';
import { gql } from '@apollo/client';
import { useFragment } from '@apollo/client/react';
import type { GridRowModelUpdate, GridValidRowModel } from '@mui/x-data-grid-premium';
import type { ReportRow } from 'src/components';
import { AiReasoningTooltipButton } from 'src/components/ai/shared/components/AiReasoningTooltipButton';
import { useCitationNavigation } from 'src/components/ai/shared/components/useCitationNavigation';
import { Apollo } from 'src/gql/generated';
import { getInternalReferenceTitleCitation } from 'src/helpers';

const childRelationshipTypeValues = new Set<string>(Object.values(Apollo.ChildRelationshipType));

export function isChildRelationshipType(value: string): value is Apollo.ChildRelationshipType {
  return childRelationshipTypeValues.has(value);
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export type RowDirection = 'parent' | 'child';

export function getRelationshipBadgeLabel(direction: RowDirection): 'PARENT' | 'CHILD' {
  return direction === 'parent' ? 'PARENT' : 'CHILD';
}

export type GridRowModelLike = GridValidRowModel | GridRowModelUpdate;

export function getParentPathFromRow(value: GridRowModelLike): string[] | null {
  if (!isRecord(value)) return null;
  const parentPath = value.parentPath;
  if (!Array.isArray(parentPath)) return null;
  if (!parentPath.every(item => typeof item === 'string')) return null;
  return parentPath;
}

export interface AIReasoningTooltipProps {
  /** Markdown-formatted reason with inline citation links */
  llmReasonsMarkdown: string | null;
  /** Threaded down to the inline citations so a cross-document jump can highlight this row. */
  relationshipId?: string | null;
}

export function getRelationshipBadgeTooltip({
  count,
  isExtracting,
  missingMatchesOnly,
  unconfirmedRelationshipsOnly,
}: {
  count: number;
  isExtracting: boolean;
  missingMatchesOnly: boolean;
  unconfirmedRelationshipsOnly: boolean;
}): string {
  if (isExtracting) return 'Detecting relationships...';
  if (missingMatchesOnly) return `${count} missing ${count === 1 ? 'match' : 'matches'}`;
  if (unconfirmedRelationshipsOnly)
    return `${count} ${count === 1 ? 'relationship' : 'relationships'} for review`;

  return `${count} ${count === 1 ? 'relationship' : 'relationships'} found`;
}

export function getActionStatus({
  reviewOutcome,
  showRelationshipActions,
}: {
  reviewOutcome: Apollo.ReviewOutcome | null;
  showRelationshipActions: boolean;
}): 'confirmed' | 'rejected' | 'unconfirmed' {
  if (showRelationshipActions) return 'unconfirmed';
  if (reviewOutcome === Apollo.ReviewOutcome.Confirmed) return 'confirmed';
  return 'rejected';
}

export function getRelationshipFromRow(row: ReportRow) {
  switch (row.__typename) {
    case 'Document':
      return row.relationship ?? null;
    case 'UrlRelationshipRow':
      return row.urlRelationship;
    case 'OrphanRelationshipRow':
      return row.orphanedRelationship;
    case 'GroupHeaderRow':
      return null;
  }
}

/**
 * These are the only fields that are really updated in the DataGrid, so we keep the
 * payload light here and only return what user's update in the DataGrid.
 */
const ROW_RELATIONSHIP_FRAGMENT = gql`
  fragment RelationshipReviewStatus on DocumentRelationship {
    id
    childRelationshipType
    childDocumentId
    parentDocumentId
    review {
      __typename
      outcome
      reviewedBy {
        id
        displayName
      }
    }
  }
`;

/**
 * Reads relationship fields (type + review status) directly from the Apollo cache.
 * After mutations update the cache, consumers of this hook re-render immediately
 * rather than waiting for a DataGrid refetch.
 */
export function useRelationship(row: ReportRow) {
  const relationship = getRelationshipFromRow(row);
  const { complete: cacheHit, data: cached } = useFragment<Apollo.RelationshipReviewStatusFragment>(
    {
      fragment: ROW_RELATIONSHIP_FRAGMENT,
      fragmentName: Apollo.namedOperations.Fragment.RelationshipReviewStatus,
      from: { __typename: 'DocumentRelationship', id: relationship?.id ?? '' },
    }
  );
  return { relationship: cacheHit ? cached : relationship };
}

/**
 * True for a self-referencing (equal-ID) relationship edge: a bound-in agreement modeled
 * as a document's relationship to itself. Mirrors the server's `isSelfReferencingEdge`
 * (packages/server/src/services/document-relationship/is-self-referencing-edge.ts).
 */
export function getIsSelfReferencingRelationship(relationship: {
  childDocumentId: string | null | undefined;
  parentDocumentId: string | null | undefined;
}): boolean {
  return Boolean(
    relationship.parentDocumentId && relationship.parentDocumentId === relationship.childDocumentId
  );
}

/**
 * Click handler for any relationship row's title (INTERNAL / CHILD / PARENT): opens that row's
 * document with the relationship's Related Documents card highlighted on arrival, optionally
 * landing on the first `#doc/...` citation in the reasons that points into that document.
 *
 * Reasons with no citation are normal — highlight-only is still a real destination. Only a click
 * with no relationship to point at returns `false`, so callers can fall back to the row's normal
 * open-document handler.
 *
 * Returns `true` when it navigated — callers should stop the click from propagating further.
 */
export function useRelationshipRowTitleClick(args: {
  documentId: UUID;
  llmReasonsMarkdown: string | null | undefined;
  relationshipId: string | null;
}): () => boolean {
  const { documentId, llmReasonsMarkdown, relationshipId } = args;
  const { navigateToRelationshipCard } = useCitationNavigation();

  return useCallback(() => {
    if (!relationshipId) return false;

    const citation = getInternalReferenceTitleCitation(llmReasonsMarkdown);
    const citationParts = citation ? parseMarkdownLocationLink(citation.href) : null;
    const sectionCitation =
      citation && citationParts?.documentId === documentId ? citation : undefined;

    navigateToRelationshipCard({
      citation: sectionCitation,
      documentId,
      relationshipId,
    });
    return true;
  }, [documentId, llmReasonsMarkdown, navigateToRelationshipCard, relationshipId]);
}

export function AIReasoningTooltip({
  llmReasonsMarkdown,
  relationshipId,
}: AIReasoningTooltipProps): JSX.Element {
  return (
    <AiReasoningTooltipButton
      emptyTitle="No insight available yet"
      llmReasonsMarkdown={llmReasonsMarkdown}
      relationshipId={relationshipId}
      size="medium"
    />
  );
}
